import React, { useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, ConfirmDialog, LayerContextProvider, layers } from "@activecollab/components";
import classNames from "classnames";
import { Form, Formik } from "formik";
import PropTypes from "prop-types";
import * as yup from "yup";
import { useFileHandler } from "~/components/Hooks";
import { useFocusInvalidField } from "~/components/Hooks/useFocusInvalidField";
import { AttachmentUpload } from "~/components/select_attachments/AttachmentUpload";
import { keyCodes } from "~/utils/key_codes";
import TemplateElementContent from "../TemplateElementContent";

const validationSchema = yup.object().shape({
  name: yup.string().required(),
});

export const TemplateDiscussionAndNoteForm = ({ initialState, onSubmit, onCancel }) => {
  const { t } = useTranslation();
  const submitButtonRef = useRef();
  const cancelButtonRef = useRef();
  const fileInputRef = useRef();
  const { fieldRef, focusFirstInvalidField } = useFocusInvalidField();
  const { uploading, onUpload } = useFileHandler();
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);

  const onFormSubmit = useCallback(
    async (values) => {
      const attributes = {
        ...values,
        ...(values.attachments.filesToAttach && {
          attach_uploaded_files: values.attachments.filesToAttach,
        }),
        ...(values.attachments.filesToDrop && {
          drop_attached_files: values.attachments.filesToDrop,
        }),
      };

      await onSubmit(attributes);
    },
    [onSubmit]
  );

  const handleKeyPress = useCallback((event) => {
    if (submitButtonRef.current && event.key === keyCodes.enter && (event.ctrlKey || event.metaKey)) {
      event.preventDefault();
      submitButtonRef.current.click();
    }
    if (cancelButtonRef.current && event.key === keyCodes.escape) {
      event.preventDefault();
      cancelButtonRef.current.click();
    }
  }, []);

  const handleConfirmModalSubmit = useCallback(() => {
    setShowConfirmationModal(false);
    onCancel();
  }, [onCancel]);

  const handleCloseConfirmationModal = useCallback(() => {
    setShowConfirmationModal(false);
  }, []);

  return (
    <>
      <Formik
        initialValues={initialState}
        onSubmit={onFormSubmit}
        validationSchema={validationSchema}
        validateOnMount
      >
        {({ isSubmitting, errors, dirty, handleSubmit }) => {
          const handleFormSubmit = (e) => {
            e.preventDefault();
            handleSubmit();

            if (Object.keys(errors)?.length) {
              return focusFirstInvalidField(errors, true);
            }
          };
          return (
            <Form
              onKeyDown={handleKeyPress}
              className={classNames("tw-flex tw-flex-col tw-w-full tw-p-8 sm:tw-pr-2")}
              data-testid="template-element-form"
              onSubmit={handleFormSubmit}
            >
              <TemplateElementContent
                fieldRef={fieldRef}
                fileInputRef={fileInputRef}
              />

              <div className="tw-py-4">
                <Button
                  ref={submitButtonRef}
                  type="submit"
                  variant="primary"
                  disabled={isSubmitting || uploading}
                  tabIndex={0}
                  className="tw-mr-3"
                  data-qa-id="add-form-button"
                >
                  {uploading ? `${t("Uploading")}...` : t("Save")}
                </Button>
                <Button
                  ref={cancelButtonRef}
                  type="button"
                  variant="secondary"
                  onClick={() => {
                    dirty ? setShowConfirmationModal(true) : onCancel();
                  }}
                  tabIndex={0}
                  data-qa-id="cancel-form-button"
                >
                  {t("Cancel")}
                </Button>
              </div>
            </Form>
          );
        }}
      </Formik>
      <LayerContextProvider value={{ zIndex: layers.skyscraper }}>
        <ConfirmDialog
          open={showConfirmationModal}
          dialogTitle={t("Discard changes?")}
          dialogContent={t("All unsaved changes will be lost.")}
          confirmBtnText={t("Discard")}
          cancelBtnText={t("Cancel")}
          onConfirm={handleConfirmModalSubmit}
          onCancel={handleCloseConfirmationModal}
        />
      </LayerContextProvider>
      <AttachmentUpload
        ref={fileInputRef}
        multiple
        onUpload={onUpload}
      />
    </>
  );
};

TemplateDiscussionAndNoteForm.propTypes = {
  initialState: PropTypes.object.isRequired,
  onSubmit: PropTypes.func,
  onCancel: PropTypes.func,
};

TemplateDiscussionAndNoteForm.defaultProps = {
  onSubmit: () => null,
  onCancel: () => null,
};
